// NeuralGraph — the graph substrate renderer (Milestone 1: resting-capable).
//
// Motion Bible Law #1: NO ambient/idle animation. The ambient "breathing" and the
// all-node idle pulse are REMOVED. At rest the ONLY motions are the single priority
// node's slow pulse (§3) and real temporal decay (§4, applied via `nodeTreatments`
// computed from real recency by the caller). Everything else is still.
//
// Real-data bindings (unchanged): every node is a real entity, every edge a real join
// (lib/brainGraphAdapter); node radius = real degree; edge width = real link-strength.
//
// Props:
//   interactive       — M1 passes false ⇒ ZERO affordances (no focus/hover/click/keys).
//   priorityNodeId    — the one node that may pulse at rest (brightest/largest).
//   nodeTreatments    — { [nodeId]: { luminance, warmth, depth, tier } } from lib/temporal.
//   brightenIds       — Set<string> nodes to gently brighten on a REAL polled delta (§5).
//   confidenceRings / illuminateSequence — reserved for M2/M3 (unused at rest).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { buildAdjacency } from "./lib/brainGraphAdapter";
import { layoutGraph } from "./lib/layout";
import { clusterColor, clusterLabel, edgeGlow, edgeProvenance, edgeWidth, nodeRadius, tierFraction } from "./tokens";
import { useReducedMotion } from "./lib/useReducedMotion";
import { decisionTransitionName } from "./lib/transition";
import { motion } from "./lib/motion";
import { EmptyState } from "./BrainStates";

const WARM = "#f5a15a"; // "warm = fresh" temporal tint (spec §8)

function ConfidenceRing({ r, ring, color, reduce }) {
  const rr = r + 6;
  const c = 2 * Math.PI * rr;
  const toFrac = tierFraction(ring.tier);
  const fromFrac = ring.hasDelta && ring.from ? tierFraction(ring.from) : toFrac;
  const dash = (f) => `${Math.max(0, Math.min(1, f)) * c} ${c}`;
  const animate = ring.hasDelta && !reduce && fromFrac !== toFrac;
  const cUp = motion("confidence-up");
  return (
    <circle r={rr} fill="none" stroke={color} strokeOpacity={0.9} strokeWidth={2} strokeLinecap="round" strokeDasharray={dash(animate ? fromFrac : toFrac)} transform="rotate(-90)" data-testid="confidence-ring" data-has-delta={ring.hasDelta ? "true" : "false"}>
      {animate ? <animate attributeName="stroke-dasharray" from={dash(fromFrac)} to={dash(toFrac)} dur={cUp.cssDuration} fill="freeze" repeatCount="1" /> : null}
    </circle>
  );
}

export default function NeuralGraph({
  graph,
  width = 900,
  height = 560,
  selectedId,
  onSelect,
  interactive = true,
  centerId = null,
  priorityNodeId = null,
  nodeTreatments = null,
  brightenIds = null,
  activatedIds = null,
  illuminateSequence = null,
  confidenceRings = null,
}) {
  const reduce = useReducedMotion();
  const [focusId, setFocusId] = useState(null);
  const [illumStep, setIllumStep] = useState(0);
  const nodeRefs = useRef({});

  const { positions } = useMemo(() => layoutGraph(graph, { width, height }), [graph, width, height]);
  const adjacency = useMemo(() => buildAdjacency(graph.edges || []), [graph.edges]);
  const nodeById = useMemo(() => new Map((graph.nodes || []).map((n) => [n.id, n])), [graph.nodes]);

  // traversal-order path illumination (M2/M3 only; inert at rest).
  const seqLen = illuminateSequence ? illuminateSequence.length : 0;
  useEffect(() => {
    if (!seqLen) return undefined;
    if (reduce) {
      setIllumStep(seqLen);
      return undefined;
    }
    setIllumStep(0);
    let i = 0;
    const step = motion("manifest").durationMs;
    const id = setInterval(() => {
      i += 1;
      setIllumStep(i);
      if (i >= seqLen) clearInterval(id);
    }, step);
    return () => clearInterval(id);
  }, [illuminateSequence, seqLen, reduce]);

  if (!graph.nodes || graph.nodes.length === 0) {
    return (
      <EmptyState
        title="A quiet mind"
        detail="No linked thoughts to show yet — nothing is drawn without a real relationship."
        testId="graph-empty"
      />
    );
  }

  const active = interactive ? focusId || selectedId || centerId : null;
  const activeNeighbors = new Set(active ? adjacency.get(active) || [] : []);
  const litTypes = illuminateSequence ? new Set(illuminateSequence.slice(0, illumStep)) : null;

  const isEdgeActive = (e) => active && (e.source === active || e.target === active);
  const isEdgeLit = (e) => (litTypes ? litTypes.has(e.type) : false);
  const nodeShown = (id) => !activatedIds || activatedIds.has(id);

  const traverse = (fromId, dir) => {
    const neigh = adjacency.get(fromId) || [];
    if (!neigh.length) return;
    const idx = neigh.indexOf(active);
    const next = dir > 0 ? neigh[(Math.max(idx, -1) + 1) % neigh.length] : neigh[(idx - 1 + neigh.length) % neigh.length];
    setFocusId(next);
    const ref = nodeRefs.current[next];
    if (ref && ref.focus) ref.focus();
  };

  const onKeyDown = (e, node) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); traverse(node.id, 1); }
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); traverse(node.id, -1); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect && onSelect(node); }
  };

  const nodeAria = (node) => {
    const neigh = adjacency.get(node.id) || [];
    const clusters = [...new Set(neigh.map((id) => clusterLabel((nodeById.get(id) || {}).cluster)))];
    const ring = confidenceRings && confidenceRings[node.id];
    const confText = ring ? ` Confidence ${ring.tier}${ring.hasDelta ? ` (changed from ${ring.from})` : ""}.` : "";
    return `${clusterLabel(node.cluster)}: ${node.label}. ${neigh.length} connection${neigh.length === 1 ? "" : "s"}${clusters.length ? ` to ${clusters.join(", ")}` : ""}.${confText}`;
  };

  // Priority pulse (the ONLY at-rest node motion): faint, ~3s, attention easing.
  const pulse = motion("priority-pulse", { reducedMotion: reduce });
  const surprise = motion("surprise-brighten", { reducedMotion: reduce });
  const attnSpline = "0.34 1.3 0.64 1";

  const priorityCount = graph.nodes.filter((n) => n.id === priorityNodeId).length;
  const summary = `Cipher at rest — ${graph.nodes.length} thoughts${priorityCount ? "; one priority is dwelt on" : ""}.`;

  return (
    <div className="w-full overflow-x-auto">
      <svg
        data-testid="neural-graph"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        role={interactive ? "group" : "img"}
        aria-label={interactive ? "Decision relationship graph. Use Tab to enter, arrow keys to traverse connections, Enter to open a node." : summary}
        className="max-w-full"
      >
        <g data-testid="graph-body">
          <g data-testid="graph-edges">
            {(graph.edges || []).map((e) => {
              const a = positions.get(e.source);
              const b = positions.get(e.target);
              if (!a || !b) return null;
              if (!nodeShown(e.source) || !nodeShown(e.target)) return null;
              const w = edgeWidth(e.weight, graph.maxWeight);
              // "Hot" (real high-weight) edges render brighter/more opaque than duller,
              // low-weight edges — same weight/maxWeight already used for width above.
              const restOpacity = edgeGlow(e.weight, graph.maxWeight);
              const lit = isEdgeLit(e);
              const on = lit || isEdgeActive(e);
              return (
                <line key={e.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke={on ? "#e2e8f0" : "#3a4657"}
                  strokeWidth={on ? w + 1 : w}
                  strokeOpacity={litTypes ? (lit ? 0.95 : 0.12) : active ? (on ? 0.9 : 0.12) : restOpacity}
                  data-testid={`edge-${e.type}`} data-lit={lit ? "true" : "false"}>
                  <title>{`${edgeProvenance(e.type).label} — ${edgeProvenance(e.type).join} (strength ${e.weight})`}</title>
                </line>
              );
            })}
          </g>
          <g data-testid="graph-nodes">
            {(graph.nodes || []).map((node) => {
              const p = positions.get(node.id);
              if (!p) return null;
              const shown = nodeShown(node.id);
              const r = nodeRadius(node.size, graph.maxSize);
              const color = clusterColor(node.cluster);
              const isPriority = node.id === priorityNodeId;
              const isActive = active === node.id;
              const isNeighbor = activeNeighbors.has(node.id);
              const dim = active && !isActive && !isNeighbor;

              const t = (nodeTreatments && nodeTreatments[node.id]) || null;
              const brightening = brightenIds && brightenIds.has(node.id);
              const baseLum = t ? t.luminance : 0.85;
              const depth = t ? t.depth : 0;
              const warmth = brightening ? Math.max(t ? t.warmth : 0, 0.9) : t ? t.warmth : 0;
              const lum = brightening ? 1 : isPriority ? Math.max(baseLum, 0.98) : baseLum;
              const scale = (isPriority ? 1.28 : 1) * (1 - depth * 0.2);
              const blurPx = +(depth * 3).toFixed(2);
              const ring = confidenceRings && confidenceRings[node.id];
              const showLabel = isPriority || (t && t.tier === "fresh") || (interactive && isActive);

              const interactiveProps = interactive
                ? {
                    tabIndex: 0,
                    role: "button",
                    "aria-label": nodeAria(node),
                    "aria-pressed": selectedId === node.id,
                    onFocus: () => setFocusId(node.id),
                    onBlur: () => setFocusId(null),
                    onClick: () => onSelect && onSelect(node),
                    onKeyDown: (e) => onKeyDown(e, node),
                    ref: (el) => { nodeRefs.current[node.id] = el; },
                    style: { cursor: onSelect ? "pointer" : "default", outline: "none" },
                  }
                : { "aria-hidden": "true", style: { pointerEvents: "none" } };

              // Surprise brighten transition (attention 600ms) or instant under reduced-motion.
              const brightenTransition = brightening && !reduce ? `opacity ${surprise.cssDuration} ${surprise.cssEasing}, filter ${surprise.cssDuration} ${surprise.cssEasing}` : reduce ? "none" : "opacity 400ms ease";

              return (
                <g
                  key={node.id}
                  data-testid={`node-${node.cluster}`}
                  data-node-id={node.id}
                  data-priority={isPriority ? "true" : "false"}
                  data-brightening={brightening ? "true" : "false"}
                  data-temporal={t ? t.tier : "none"}
                  transform={`translate(${p.x} ${p.y}) scale(${scale})`}
                  {...interactiveProps}
                  style={{
                    ...(interactiveProps.style || {}),
                    opacity: !shown ? 0 : dim ? 0.25 : 1,
                    filter: blurPx > 0.05 ? `blur(${blurPx}px)` : undefined,
                    transition: brightenTransition,
                  }}
                >
                  {/* Static glow halo — size and brightness trace to the node's REAL radius
                      (nodeRadius, from real degree/size) and its REAL luminance treatment
                      (nodeTreatments, from real recency/priority). Bigger, more-central,
                      or brighter nodes glow more; this is a static blur/shadow, not a new
                      animation loop — Motion Bible Law #1 (no idle motion) is unaffected. */}
                  {shown ? (
                    <circle
                      r={+(r * 1.85).toFixed(2)}
                      fill={color}
                      fillOpacity={+(0.12 + Math.max(0, Math.min(1, lum)) * 0.2).toFixed(3)}
                      style={{ filter: `blur(${+(Math.max(2, r * 0.45)).toFixed(2)}px)` }}
                      pointerEvents="none"
                    />
                  ) : null}
                  {warmth > 0.02 ? <circle r={r + 4} fill={WARM} fillOpacity={+(warmth * 0.32).toFixed(3)} /> : null}
                  {ring ? <ConfidenceRing r={r} ring={ring} color={color} reduce={reduce} /> : null}
                  {isPriority ? <circle r={r + 5} fill="none" stroke={color} strokeWidth={2} strokeOpacity={0.9} /> : null}
                  <circle r={r} fill={color} fillOpacity={Math.max(0, Math.min(1, lum))}>
                    {isPriority && !reduce ? (
                      <animate
                        attributeName="fill-opacity"
                        values={`${lum};${(lum * 0.72).toFixed(3)};${lum}`}
                        keyTimes="0;0.5;1"
                        calcMode="spline"
                        keySplines={`${attnSpline};${attnSpline}`}
                        dur={pulse.cssDuration}
                        repeatCount="indefinite"
                      />
                    ) : null}
                  </circle>
                  {showLabel ? (
                    <text y={r + 12} textAnchor="middle" fontSize="10" fill="#cbd5e1" fillOpacity={Math.max(0.5, lum)} pointerEvents="none">
                      {String(node.label).slice(0, 22)}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        </g>
      </svg>
    </div>
  );
}
